"use client";

import { Typography } from "@tosize/ui";
import { usePathname } from "next/navigation";
import { Fragment } from "react";
import { Link, StyledLink } from "~/components/Link";
import type { CategoryPageType } from "~/graphql/shared/category";

interface BarProps {
	category: CategoryPageType;
}

export function TopBar({ category }: BarProps) {
	const pathname = usePathname();
	const children = category.children.filter((child) => child.isUsedForSeo === 1);
	const parentChildren = category.parent.children.filter((child) => child.isUsedForSeo === 1);

	const categories = children.length ? children : parentChildren;

	if (!categories?.length) return null;

	return (
		<div className="mb-4 rounded bg-shades-light px-4 py-2">
			<div className="scrollbar-none flex snap-x flex-nowrap space-x-6 overflow-x-auto overflow-y-hidden">
				{categories.map((item) => (
					<div key={item.id} className="snap-start whitespace-nowrap leading-none">
						{pathname === item.url ? (
							<Typography size="text-sm">{item.name}</Typography>
						) : (
							<StyledLink href={item.url} className="text-sm">
								{item.name}
							</StyledLink>
						)}
					</div>
				))}
			</div>
		</div>
	);
}

type BottomBarProps = BarProps & {
	subtitle: string;
};

export function BottomBar({ category, subtitle }: BottomBarProps) {
	const pathname = usePathname();
	const children = category.children.filter((child) => child.isUsedForSeo === 2);
	const parentChildren = category.parent.children.filter((child) => child.isUsedForSeo === 2);

	const categories = children.length ? children : parentChildren;

	if (!categories?.length) return null;

	return (
		<div className="flex flex-col gap-4 md:flex-row md:items-center">
			<Typography className="whitespace-nowrap">{subtitle}:</Typography>
			<div className="scrollbar-none flex w-full snap-x flex-nowrap space-x-4 overflow-x-auto overflow-y-hidden">
				{categories.map((item) => (
					<Fragment key={item.id}>
						{pathname === item.url ? (
							<Typography
								size="text-sm"
								className="snap-start whitespace-nowrap rounded-3xl border border-shades bg-shades-light px-4 py-2 leading-[20px]"
							>
								{item.name}
							</Typography>
						) : (
							<Link
								href={item.url ?? "/"}
								className="snap-start whitespace-nowrap rounded-3xl border border-shades px-4 py-2 text-sm leading-[20px] hover:bg-shades-light focus:outline-none focus-visible:bg-shades-light"
							>
								{item.name}
							</Link>
						)}
					</Fragment>
				))}
			</div>
		</div>
	);
}
